<template>
    <!-- The provider's bar, exactly the prototype's: Imprion Guest logo on the house's
         primary colour, the pages, the theme toggle, the guest's name. The customizer
         gear appears only in studio mode (?studio) — the look is the house's setting,
         not the guest's. -->
    <header class="top">
        <div class="brand" style="min-width:205px">
            <img
                src="/assets/imprion-guest-logo-inverse.png"
                alt="Imprion Guest"
                style="display:block;width:190px;height:58px;object-fit:contain;object-position:left center;mix-blend-mode:screen"
            >
        </div>

        <nav v-if="!invalidState && !standalone" class="nav">
            <router-link
                v-for="page in pages"
                :key="page.name"
                :to="{ name: page.name }"
                :class="{ active: route.name === page.name }"
            >{{ $t(`nav.${page.name}`) }}</router-link>
        </nav>

        <button
            class="theme-toggle"
            :aria-label="dark ? $t('theme.to_light') : $t('theme.to_dark')"
            :title="$t('theme.title')"
            @click="toggleTheme"
        >{{ dark ? '☀' : '☾' }}</button>

        <button v-if="studio" class="customize" @click="customizeOpen = true">
            ⚙ <span>{{ $t('studio.button') }}</span>
        </button>

        <!-- The telephone's menu: the top row's pages do not fit a narrow screen, and
             the bottom bar has room for six, so everything is one press away here. -->
        <button
            v-if="!invalidState && !standalone"
            class="menu-toggle"
            :aria-label="$t('nav.menu')"
            :aria-expanded="menuOpen"
            @click="menuOpen = !menuOpen"
        >☰</button>

        <div v-if="guestName && !invalidState && !standalone" class="user">
            <span>{{ guestName }}</span>
            <div class="avatar">{{ initials(guestName) }}</div>
        </div>
    </header>

    <main class="shell">
        <!-- The hotel has not bought this module: not a dead link, and not to be
             shown as one — a new link would open the same refusal. Checked before
             `failed`, which the same 403 also sets. -->
        <section v-if="moduleUnlicensed" class="page on">
            <ModuleNotice />
        </section>

        <!-- One refusal for every reason a link does not open. A stranger learns
             nothing; a guest learns whom to ask. -->
        <section v-else-if="appStore.failed" class="page on">
            <InvalidState />
        </section>

        <router-view v-else />

        <!-- What is actually running, at the bottom of every page. Small and muted:
             it is here for the one telephone call where somebody has to say which
             build they are looking at, not for the guest. -->
        <AppVersion />
    </main>

    <!-- The telephone's bottom bar: five doors, always under a thumb. -->
    <nav v-if="!invalidState && !standalone" class="bottom">
        <router-link
            v-for="page in bottomPages"
            :key="page.name"
            :to="{ name: page.name }"
            :class="{ active: route.name === page.name }"
        ><b>{{ page.glyph }}</b>{{ $t(`nav.short_${page.name}`) }}</router-link>
    </nav>

    <!-- The menu itself: every page of the stay, room service among them after the keys. -->
    <div class="menu-bg" :class="{ open: menuOpen }" @click.self="menuOpen = false">
        <nav class="menu-drawer" :aria-label="$t('nav.menu')">
            <div class="menu-head">
                <b>{{ $t('nav.menu') }}</b>
                <button class="menu-close" :aria-label="$t('general.close')" @click="menuOpen = false">×</button>
            </div>

            <router-link
                v-for="page in menuPages"
                :key="page.name"
                :to="{ name: page.name }"
                class="menu-link"
                :class="{ active: route.name === page.name }"
            ><span class="menu-glyph">{{ page.glyph }}</span>{{ $t(`nav.${page.name}`) }}</router-link>
        </nav>
    </div>

    <StudioModal v-if="studio" v-model:open="customizeOpen" />

    <!-- Milo, mounted ONCE on the shell and not per screen: the sheet holds a
         conversation, and a conversation that were re-created on every page change would
         be a guest losing what they had typed by looking at their bill. -->
    <template v-if="miloShown">
        <MiloButton />
        <MiloSheet />
    </template>

    <!-- The house's ordering PIN, asked before an order or a booking (libs/orderPin.ts). -->
    <OrderPinModal />

    <div class="toast" :class="{ show: toastShown }">{{ toastMessage }}</div>
</template>

<script lang="ts" setup>
    import { computed, onMounted, ref, watch } from 'vue'
    import { useRoute, useRouter } from 'vue-router'

    import { hasToken } from '@/libs/api'

    import AppVersion from '@/components/AppVersion.vue'
    import InvalidState from '@/components/InvalidState.vue'
    import MiloButton from '@/components/MiloButton.vue'
    import MiloSheet from '@/components/MiloSheet.vue'
    import ModuleNotice from '@/components/ModuleNotice.vue'
    import OrderPinModal from '@/components/OrderPinModal.vue'
    import StudioModal from '@/components/StudioModal.vue'
    import { moduleUnlicensed } from '@/libs/license'
    import { toastMessage, toastShown } from '@/libs/toast'
    import { useAppStore } from '@/stores/app'
    import { useChatStore } from '@/stores/chat'

    const route = useRoute()
    const router = useRouter()
    const appStore = useAppStore()
    const chatStore = useChatStore()
    // Everything that hides the navigation: a link that does not open, and a hotel
    // that does not offer this at all. Tabs to pages that are every one of them
    // refused are worse than no tabs.
    const invalidState = computed(() => route.name === 'invalid' || appStore.failed || moduleUnlicensed.value)

    // The offer and the rooming pages are somebody else's doors — a client holding
    // a link, not a guest holding a stay. The stay's navigation would only lead
    // them to refusals, so those pages stand alone: the house's bar, the page,
    // nothing else.
    //
    // The memory is here for the same reason from the other end of the stay
    // (docs/POTEK_GOSTA.md §9b): the service refuses every one of those doors once the
    // stay is over, so tabs to eight pages that are all refused would be worse than no
    // tabs — which is the argument invalidState already makes one line above.
    const standalone = computed(() => ['offer', 'rooming', 'memory'].includes(String(route.name)))

    /**
     * Where Milo is not.
     *
     * The four screens are the ones that are not a STAY: a dead link, the page a card
     * payment returns to, and the two somebody else's link opens — a client reading an
     * offer, a group leader typing in names. Milo answers about one booking behind one
     * token, and on those pages there is either no booking or no guest, so a button
     * offering to help would be offering something that cannot be given.
     *
     * `chatStore.available` carries the rest: the house has to have switched him on, and
     * a portal drawing the dead-link screen or the unlicensed notice must not have a
     * floating button over it — that would be the one thing on the screen still offering
     * to do something.
     */
    const miloShown = computed(() =>
        chatStore.available && appStore.mode !== 'memory' &&
        !['invalid', 'payment-done', 'offer', 'rooming', 'memory'].includes(String(route.name)))

    const pages = [
        { name: 'overview' }, { name: 'checkin' }, { name: 'stay' },
        { name: 'arrival' }, { name: 'keys' }, { name: 'services' }, { name: 'roomservice' }, { name: 'house' }, { name: 'payments' },
    ]

    const bottomPages = [
        { name: 'overview', glyph: '⌂' },
        { name: 'checkin',  glyph: '✓' },
        { name: 'arrival',  glyph: '⌖' },
        { name: 'keys',     glyph: '⚿' },
        { name: 'services', glyph: '✦' },
        { name: 'payments', glyph: '€' },
    ]

    /** Every page of the stay for the telephone's menu. */
    const menuPages = [
        { name: 'overview', glyph: '⌂' },
        { name: 'checkin',  glyph: '✓' },
        { name: 'stay',     glyph: '☾' },
        { name: 'arrival',  glyph: '⌖' },
        { name: 'keys',     glyph: '⚿' },
        { name: 'roomservice', glyph: '☕' },
        { name: 'services', glyph: '✦' },
        { name: 'house',    glyph: '⌂' },
        { name: 'payments', glyph: '€' },
    ]

    const menuOpen = ref(false)

    // A page chosen is the menu's job done.
    watch(() => route.name, () => { menuOpen.value = false })

    // Studio mode: the prototype's customizer, preview only, for whoever is designing a
    // house's look. A guest never sees the gear.
    const studio = computed(() => new URLSearchParams(window.location.search).has('studio'))
    const customizeOpen = ref(false)

    const guestName = computed(() => {
        const rooms = appStore.stay?.rooms ?? []

        return rooms.flatMap(room => room.guests)[0]?.name ?? ''
    })

    const initials = (name: string): string =>
        name.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]?.toUpperCase() ?? '').join('')

    const dark = ref(document.body.classList.contains('dark'))

    const toggleTheme = () => {
        dark.value = !dark.value
        document.body.classList.toggle('dark', dark.value)
        localStorage.setItem('imprionGuestTheme', dark.value ? 'dark' : 'light')
    }

    // Which of the link's two lives this is can only be known once the stay read has
    // answered, and the router guard that redirects to the memory cannot wait for it. So
    // the correction happens here, once, when the answer arrives: a guest who opened a
    // two-year-old link sees the overview for as long as one request takes and then the
    // page that is actually theirs.
    watch(() => appStore.mode, (mode) => {
        if (mode === 'memory' && route.name !== 'memory') void router.replace({ name: 'memory' })
    })

    // The stay is loaded here and not per page: a guest who lands straight on Keys or
    // Payments — a refresh, a bookmark — must not find an empty screen because they
    // skipped the overview.
    onMounted(() => {
        dark.value = document.body.classList.contains('dark')

        if (hasToken() && !appStore.stay && !appStore.memory) void appStore.load()
    })
</script>
